/**
 * `/oauth/consent` — where Better Auth's OAuth provider sends a browser to approve
 * an application (`modules/auth/auth.ts` `OAUTH_CONSENT_PAGE`, and it is
 * `packages/shared`'s constant on both sides).
 *
 * Three lines of policy, and each is a decision:
 *
 *   1. **No `validateSearch`.** Every other route in this app declares one, and
 *      `routes.test.tsx` walks the tree to prove it; this one must not. The query
 *      here is not the page's state — it is an authorization request the provider
 *      signed, and `search/useSearchState.ts` would hand the page a parsed object
 *      with the repeats collapsed out of it. `auth/oauth.ts` is the file that says
 *      what that costs.
 *   2. **`currentSearch()`, not `location.href`.** The router's copy of the query is
 *      re-stringified from that same parse, so it is wrong in exactly the way that
 *      matters, and under a memory history it is not the address bar at all.
 *      `integrations/navigate.ts` is where the address bar lives.
 *   3. **Anonymous goes to `/login` and comes back here.** Not a rule of its own:
 *      `/login` already carries `?redirect=` back to wherever somebody was, and
 *      `safeRedirect` keeps the query string on it — so the signed request survives
 *      the sign-in and this page is what the magic link lands on. The other half of
 *      that, for the person the provider sent to `/login` *directly*, is
 *      `routes/login.tsx` §resuming an OAuth flow.
 *
 * Staff and client users both get the page. Which of them may grant anything is the
 * page's own question — `oauthTokenResolver` refuses a client user's token, so the
 * honest screen for them is a refusal with a working *Cancel*, not a redirect that
 * leaves the application waiting.
 */

import { OAUTH_CONSENT_PATH } from '@fareast/shared'
import { createFileRoute, redirect } from '@tanstack/react-router'

import { currentSearch } from '../../integrations/navigate.js'
import { ConsentPage } from '../../pages/oauth/ConsentPage.js'

export const Route = createFileRoute('/oauth/consent')({
  beforeLoad: ({ context }) => {
    if (context.session.principal.kind !== 'anonymous') return
    throw redirect({
      to: '/login',
      search: { redirect: `${OAUTH_CONSENT_PATH}${currentSearch()}` },
    })
  },
  component: ConsentRoute,
})

function ConsentRoute() {
  return <ConsentPage search={currentSearch()} />
}
